import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AddNewNodeIcon, ColorPickerIcon, FocusIcon } from './icons';
import { positionNodeActionCapsule } from '../../utils/mindmap/nodeActionCapsule';
import './NodeActionCapsule.css';

const MoreIcon = () => (
  <svg className="node-action-capsule__line-icon" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="5" cy="12" r="1.5" />
    <circle cx="12" cy="12" r="1.5" />
    <circle cx="19" cy="12" r="1.5" />
  </svg>
);

function visibleOcclusions(containerRect) {
  if (typeof document === 'undefined' || typeof window === 'undefined') return [];
  return [...document.querySelectorAll('[data-viewport-occlusion="true"]')]
    .filter((element) => {
      const style = window.getComputedStyle(element);
      return (
        style.display !== 'none'
        && style.visibility !== 'hidden'
        && Number.parseFloat(style.opacity || '1') > 0.05
        && element.getClientRects().length > 0
      );
    })
    .map((element) => element.getBoundingClientRect())
    .map((rect) => ({
      x: rect.left - containerRect.left,
      y: rect.top - containerRect.top,
      width: rect.width,
      height: rect.height,
    }));
}

function CapsuleButton({ action, isVisible, onActivate }) {
  return (
    <button
      type="button"
      className="node-action-capsule__button"
      aria-label={action.label}
      title={action.label}
      aria-haspopup={action.opensMenu ? 'menu' : undefined}
      tabIndex={isVisible ? 0 : -1}
      onClick={(event) => {
        event.stopPropagation();
        onActivate(action);
      }}
    >
      {action.icon}
    </button>
  );
}

function NodeActionCapsule({
  nodeId,
  nodeLabel,
  anchor,
  container,
  isDarkMode = false,
  isVisible = true,
  positioningKey,
  canWrite = true,
  canChangeColor = true,
  onAddChild,
  onChangeColor,
  onFocusBranch,
  showFocusAction = false,
  onOpenMenu,
  onReturnFocus,
  getUsableViewportGeometry,
}) {
  const capsuleRef = useRef(null);
  const [position, setPosition] = useState(null);

  const actions = useMemo(() => [
    canWrite && {
      id: 'child',
      label: 'Add idea',
      icon: <AddNewNodeIcon />,
      run: onAddChild,
    },
    canWrite && canChangeColor && {
      id: 'colour',
      label: 'Change color',
      icon: <ColorPickerIcon />,
      run: onChangeColor,
    },
    showFocusAction && {
      id: 'focus',
      label: 'Focus on this branch',
      icon: <FocusIcon />,
      run: onFocusBranch,
    },
    {
      id: 'more',
      label: 'More idea actions',
      icon: <MoreIcon />,
      run: onOpenMenu,
      opensMenu: true,
    },
  ].filter(Boolean), [
    canChangeColor,
    canWrite,
    onAddChild,
    onChangeColor,
    onFocusBranch,
    showFocusAction,
    onOpenMenu,
  ]);

  useLayoutEffect(() => {
    if (!anchor || !container || !capsuleRef.current) return undefined;
    let frameId = null;
    const updatePosition = () => {
      frameId = null;
      const anchorRect = anchor.getBoundingClientRect?.();
      const containerRect = container.getBoundingClientRect?.();
      const capsuleRect = capsuleRef.current?.getBoundingClientRect?.();
      if (!anchorRect || !containerRect || !capsuleRect) return;
      const geometry = getUsableViewportGeometry?.();
      const usableBounds = geometry?.screenBounds || {
        x: 8,
        y: 8,
        width: Math.max(0, containerRect.width - 16),
        height: Math.max(0, containerRect.height - 16),
      };
      const next = positionNodeActionCapsule({
        anchorRect: {
          x: anchorRect.left - containerRect.left,
          y: anchorRect.top - containerRect.top,
          width: anchorRect.width,
          height: anchorRect.height,
        },
        capsuleSize: {
          width: capsuleRect.width,
          height: capsuleRect.height,
        },
        bounds: usableBounds,
        obstacles: geometry?.screenObstacles || visibleOcclusions(containerRect),
      });
      if (next) {
        setPosition({
          ...next,
          overflowDirection: next.y > usableBounds.y + usableBounds.height / 2
            ? 'up'
            : 'down',
        });
      }
    };
    const schedulePosition = () => {
      if (frameId !== null) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(updatePosition);
    };
    schedulePosition();
    const anchorElement = anchor.getDOM?.();
    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(schedulePosition)
      : null;
    if (anchorElement) observer?.observe(anchorElement);
    observer?.observe(container);
    window.addEventListener('resize', schedulePosition);
    window.visualViewport?.addEventListener?.('resize', schedulePosition);
    return () => {
      if (frameId !== null) cancelAnimationFrame(frameId);
      observer?.disconnect();
      window.removeEventListener('resize', schedulePosition);
      window.visualViewport?.removeEventListener?.('resize', schedulePosition);
    };
  }, [
    actions.length,
    anchor,
    container,
    getUsableViewportGeometry,
    positioningKey,
  ]);

  if (!actions.length) return null;

  const activate = (action) => {
    action.run?.(nodeId);
  };
  const colors = isDarkMode
    ? {
        background: 'rgba(31, 41, 51, 0.96)',
        border: '#64748b',
        foreground: '#f8fafc',
        hover: '#374151',
      }
    : {
        background: 'rgba(255, 255, 255, 0.97)',
        border: '#cbd5e1',
        foreground: '#111827',
        hover: '#eef2f7',
      };

  return (
    <div
      ref={capsuleRef}
      className="node-action-capsule"
      role="toolbar"
      aria-label={`Actions for ${(nodeLabel || '').trim() || 'untitled idea'}`}
      data-testid="node-action-capsule"
      data-action-node-id={nodeId}
      data-corner={position?.corner || 'left'}
      data-vertical={position?.vertical || 'above'}
      data-overflow-direction={position?.overflowDirection || 'down'}
      data-visible={isVisible ? 'true' : 'false'}
      data-export-ignore="true"
      aria-hidden={!isVisible}
      style={{
        left: `${position?.x || 0}px`,
        top: `${position?.y || 0}px`,
        visibility: position ? 'visible' : 'hidden',
        '--node-action-background': colors.background,
        '--node-action-border': colors.border,
        '--node-action-foreground': colors.foreground,
        '--node-action-hover': colors.hover,
      }}
      onMouseDown={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === 'Escape') {
          event.preventDefault();
          onReturnFocus?.();
        }
      }}
    >
      {actions.map((action) => (
        <CapsuleButton
          key={action.id}
          action={action}
          isVisible={isVisible}
          onActivate={activate}
        />
      ))}
    </div>
  );
}

export default NodeActionCapsule;
